import { defaultParseSearch } from '@tanstack/react-router';

/**
 * Used by the router for all URLs. Keep TanStack's normal query value types,
 * except promotion codes: these are identifiers, not numbers or flags.
 *
 * ?count=2&enabled=false -> { count: 2, enabled: false }
 * ?promotion_code=true -> { promotion_code: 'true' }
 * ?promotion_code=12345678901234567890 keeps every digit as text, without rounding.
 *
 * TanStack can also serialize the string '123' as promotion_code=%22123%22.
 * Its parser already removes those JSON quotes, so keep that decoded string.
 */
export function parseSearch(search: string) {
  const parsed: Record<string, unknown> = defaultParseSearch(search);

  const rawPromotionCode = new URLSearchParams(search).get('promotion_code');
  if (rawPromotionCode !== null && typeof parsed.promotion_code !== 'string') {
    parsed.promotion_code = rawPromotionCode;
  }

  return parsed;
}

/**
 * Restore Auth0's saved appState.returnTo before navigating back to the app.
 * Unlike router parsing, the existing callback restores query values as strings:
 *
 * ?count=2&enabled=false -> { count: '2', enabled: 'false' }
 *
 * Checkout's promotion code needs one exception. For promotion_code=%22123%22,
 * URLSearchParams returns '"123"' (quotes included). Navigating with that value
 * would encode the quotes again and leave them visible in the input.
 *
 * Reuse parseSearch to recover '123', but only for checkout's promotion_code.
 * Do not parse the whole return query: that would change other login flows.
 * OAuth's code parameter and every other query value keep their existing handling.
 */
export function parseAuthReturnSearch(url: URL) {
  const searchParams = new URLSearchParams(url.search);
  const search = Object.fromEntries(searchParams.entries());

  if (url.pathname.replace(/\/$/, '') === '/store/checkout') {
    const code = parseSearch(url.search).promotion_code;
    if (typeof code === 'string') {
      search.promotion_code = code;
    }
  }

  return search;
}
